import { Injectable } from '@angular/core';

export type DeviceType = 'mobile' | 'tablet' | 'desktop';
export type DesktopAgentOs = 'windows' | 'windows_legacy' | 'mac' | 'linux';

interface NavigatorUADataLike {
  platform?: string;
  getHighEntropyValues?(hints: string[]): Promise<{ platformVersion?: string }>;
}

/**
 * Chromium Client Hints map Windows 7/8/8.1 to 0.1 / 0.2 / 0.3.
 * Frozen UA strings often report NT 10.0 even on those OS versions.
 */
export function isLegacyWindowsPlatformVersion(
  platformVersion: string | null | undefined
): boolean {
  if (!platformVersion) {
    return false;
  }
  const major = parseInt(String(platformVersion).split('.')[0], 10);
  return major === 0;
}

/** Classify the desktop agent package from a user-agent string (not phone/tablet). */
export function detectDesktopAgentOsFromUa(userAgent: string): DesktopAgentOs {
  const ua = (userAgent || '').toLowerCase();
  const isIphoneOrIpad = /iphone|ipad/.test(ua);
  if ((ua.includes('macintosh') || ua.includes('mac os x')) && !isIphoneOrIpad) {
    return 'mac';
  }
  if (ua.includes('linux') && !ua.includes('android')) {
    return 'linux';
  }
  if (/windows nt 6\.[123]/.test(ua)) {
    return 'windows_legacy';
  }
  if (ua.includes('windows')) {
    return 'windows';
  }
  return 'windows';
}

/** UA string plus optional Client Hints — catches Win 7/8 when Chrome freezes NT 10.0. */
export function detectDesktopAgentOsFromHints(
  userAgent: string,
  hints?: { platformVersion?: string | null }
): DesktopAgentOs {
  const fromUa = detectDesktopAgentOsFromUa(userAgent);
  if (fromUa === 'windows_legacy') {
    return 'windows_legacy';
  }
  if (fromUa === 'windows' && isLegacyWindowsPlatformVersion(hints?.platformVersion)) {
    return 'windows_legacy';
  }
  return fromUa;
}

@Injectable({
  providedIn: 'root'
})
export class DeviceDetectionService {
  private currentDeviceType: DeviceType | null = null;
  /** High-entropy UA-CH platform version, e.g. "0.1.0" for Windows 7. */
  private platformVersionHint: string | null = null;

  constructor() {
    this.prefetchPlatformVersionHint();
  }

  private prefetchPlatformVersionHint(): void {
    const nav = typeof navigator === 'undefined' ? null : navigator;
    const uad = (nav as Navigator & { userAgentData?: NavigatorUADataLike })?.userAgentData;
    if (!uad || typeof uad.getHighEntropyValues !== 'function') {
      return;
    }
    if (String(uad.platform || '') !== 'Windows') {
      return;
    }
    uad
      .getHighEntropyValues(['platformVersion'])
      .then((values) => {
        this.platformVersionHint = values?.platformVersion ?? null;
      })
      .catch(() => {
        // Client hints unavailable — UA string remains the fallback.
      });
  }

  /**
   * Detect the device type based on screen size and user agent
   * @returns DeviceType - 'mobile', 'tablet', or 'desktop'
   */
  detectDeviceType(): DeviceType {
    const userAgent = navigator.userAgent.toLowerCase();
    const screenWidth = window.innerWidth;

    // Check for mobile devices
    const isMobileAgent = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    
    // Check for tablet devices
    const isTabletAgent = /ipad|android(?!.*mobile)|tablet|kindle|silk|playbook/i.test(userAgent);

    // Width alone must not mark a desktop browser as tablet — a ~900px window
    // (DevTools docked, split screen) used to flip Collection to the phone stub
    // and look like a frozen Purpose→Collection Continue.
    const coarsePointer =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches;
    if (isMobileAgent && screenWidth < 768) {
      this.currentDeviceType = 'mobile';
    } else if (isTabletAgent || (coarsePointer && screenWidth >= 768 && screenWidth < 1024)) {
      this.currentDeviceType = 'tablet';
    } else {
      this.currentDeviceType = 'desktop';
    }

    console.log(`[DeviceDetection] Detected device type: ${this.currentDeviceType}`);
    return this.currentDeviceType;
  }

  /**
   * Check if the current device is mobile
   */
  isMobile(): boolean {
    return this.getDeviceType() === 'mobile';
  }

  /**
   * Check if the current device is tablet
   */
  isTablet(): boolean {
    return this.getDeviceType() === 'tablet';
  }

  /**
   * Check if the current device is desktop
   */
  isDesktop(): boolean {
    return this.getDeviceType() === 'desktop';
  }

  /**
   * Check if the current device is mobile or tablet
   */
  isMobileOrTablet(): boolean {
    const deviceType = this.getDeviceType();
    return deviceType === 'mobile' || deviceType === 'tablet';
  }

  /**
   * Get the current device type
   */
  getDeviceType(): DeviceType {
    if (!this.currentDeviceType) {
      this.detectDeviceType();
    }
    return this.currentDeviceType!;
  }

  /**
   * Desktop private-test / employee installer OS. Callers must not download when
   * `isMobileOrTablet()` is true — those devices cannot run a desktop agent.
   */
  detectDesktopAgentOs(): DesktopAgentOs {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    return detectDesktopAgentOsFromHints(ua, { platformVersion: this.platformVersionHint });
  }

  /**
   * Store device type in localStorage
   */
  storeDeviceType(): void {
    const deviceType = this.detectDeviceType();
    localStorage.setItem('deviceType', deviceType);
  }

  /**
   * Get device type from localStorage
   */
  getStoredDeviceType(): DeviceType | null {
    return localStorage.getItem('deviceType') as DeviceType | null;
  }
}

